import { AuthProvider as AuthProviderEnum } from '@40seas/entities/auth';
import { AuthProvider as AuthV2Provider, getAuthProvider, useAuth as useAuthV2 } from '@40seas/auth-client';
import { SetupModal } from '@40seas/frontend/libs/entities';
import { SafeAuth0Provider, useAuth0ContextValue } from '@40seas/frontend/libs/hooks';

import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { WebAuth } from 'auth0-js';
import * as React from 'react';
import { useMemo } from 'react';
import { LoginContext } from '../contexts/LoginContext';
import { LoginResult } from '../types/types';
import { useIFrameAuth } from './useIFrameAuth';

export const AuthProvider = ({ isAuthenticationEnabled, children }: { isAuthenticationEnabled?: boolean; children: React.ReactNode }) => {
    const setup = (window as any).__SETUP__ as SetupModal;

    if (!isAuthenticationEnabled) {
        return <>{children}</>;
    }

    const authProvider = getAuthProvider();

    if (authProvider === AuthProviderEnum.SelfManaged) {
        return (
            <AuthV2Provider>
                <SelfManagedSafeAuth0Bridge>
                    <SelfManagedLoginContextProvider>{children}</SelfManagedLoginContextProvider>
                </SelfManagedSafeAuth0Bridge>
            </AuthV2Provider>
        );
    }

    return (
        <Auth0Provider
            cookieDomain={process.env.COOKIE_DOMAIN}
            domain={(setup.auth0 && setup.auth0.domain) || ''}
            clientId={(setup.auth0 && setup.auth0.clientId) || ''}
            redirectUri={window.location.href}
            useRefreshTokens={true}
            onRedirectCallback={(_appState: any) => {}}
        >
            <SafeAuth0Bridge>
                <LoginContextProvider popupOrigin="app">{children}</LoginContextProvider>
            </SafeAuth0Bridge>
        </Auth0Provider>
    );
};

/**
 * Bridge component that exposes Auth0 context through SafeAuth0Provider.
 * This allows useSafeAuth0 to work in payment apps that use Auth0.
 */
const SafeAuth0Bridge = ({ children }: { children: React.ReactNode }) => {
    const auth0 = useAuth0();
    const auth0ContextValue = useAuth0ContextValue(auth0);

    return <SafeAuth0Provider value={auth0ContextValue}>{children}</SafeAuth0Provider>;
};

/**
 * Bridge component that exposes auth-client context through SafeAuth0Provider.
 * This allows useSafeAuth0 to work in payment apps that use self-managed auth.
 */
const SelfManagedSafeAuth0Bridge = ({ children }: { children: React.ReactNode }) => {
    const auth = useAuthV2();

    const safeAuth0ContextValue = React.useMemo(
        () => ({
            isAuthenticated: auth.isAuthenticated,
            isLoading: auth.isLoading,
            user: auth.user
                ? {
                      sub: auth.user.id,
                      email: auth.user.email,
                      name: auth.user.name ?? undefined,
                      picture: auth.user.picture ?? undefined,
                  }
                : undefined,
            loginWithRedirect: auth.loginWithRedirect,
            loginWithPopup: auth.loginWithRedirect,
            logout: auth.logout,
            getAccessTokenSilently: auth.getAccessTokenSilently,
        }),
        [auth.isAuthenticated, auth.isLoading, auth.user, auth.loginWithRedirect, auth.logout, auth.getAccessTokenSilently],
    );

    return <SafeAuth0Provider value={safeAuth0ContextValue}>{children}</SafeAuth0Provider>;
};

/**
 * LoginContext provider for self-managed auth in payment apps.
 * Provides login methods that use the self-managed auth service instead of Auth0.
 */
const SelfManagedLoginContextProvider = ({ children }: { children: React.ReactNode }) => {
    const { loginWithGoogle, loginWithMicrosoft, loginWithPassword, register, resendVerification, verifyCode } = useAuthV2();

    const contextValue = React.useMemo(
        () => ({
            loginWithGoogle: async () => {
                try {
                    return await loginWithGoogle();
                } catch (error) {
                    throw error instanceof Error ? error.message : String(error);
                }
            },
            loginWithMicrosoft: async () => {
                try {
                    return await loginWithMicrosoft();
                } catch (error) {
                    throw error instanceof Error ? error.message : String(error);
                }
            },
            login: async (email: string, password: string) => {
                try {
                    const response = await loginWithPassword(email, password);
                    return { accessToken: response.tokens.accessToken };
                } catch (error) {
                    throw error instanceof Error ? error.message : String(error);
                }
            },
            signup: async (email: string, password: string) => {
                try {
                    const response = await register(email, password);
                    // Self-managed auth always requires email verification before login
                    return { requiresEmailVerification: true as const, email: response.email };
                } catch (error) {
                    throw error instanceof Error ? error.message : String(error);
                }
            },
            resendVerification: async (email: string) => {
                try {
                    await resendVerification(email);
                } catch (error) {
                    throw error instanceof Error ? error.message : String(error);
                }
            },
            verifyCode: async (email: string, code: string) => {
                try {
                    const response = await verifyCode(email, code);
                    return { accessToken: response.tokens.accessToken };
                } catch (error) {
                    throw error instanceof Error ? error.message : String(error);
                }
            },
        }),
        [loginWithGoogle, loginWithMicrosoft, loginWithPassword, register, resendVerification, verifyCode],
    );

    return <LoginContext.Provider value={contextValue}>{children}</LoginContext.Provider>;
};

export const LoginContextProvider = ({ popupOrigin, children }: { popupOrigin: 'dashboard' | 'app'; children: React.ReactNode }) => {
    const setup = (window as any).__SETUP__ as SetupModal;

    const { signup, login } = useIFrameAuth(popupOrigin);

    const webAuth = useMemo(
        () =>
            new WebAuth({
                domain: setup.auth0!.domain,
                clientID: setup.auth0!.clientId,
                redirectUri: process.env.AUTH_REDIRECT_URL,
                cookieDomain: process.env.COOKIE_DOMAIN,
                scope: 'openid profile email',
                responseType: 'token',
                popupOrigin: popupOrigin === 'dashboard' ? process.env.DASHBOARD_ORIGIN : process.env.APP_ORIGIN,
            }),
        [],
    );

    const loginWithSocial = (connection: 'google-oauth2' | 'azure-40Seas') => {
        return new Promise<LoginResult>((_resolve, _reject) =>
            webAuth.authorize({
                connection,
                domain: setup.auth0!.domain,
                responseType: 'token',
                redirectUri: process.env.AUTH_CALLBACK_URL! + `?origin=${popupOrigin}`,
            }),
        );
    };

    const loginWithSocialPopup = (connection: 'google-oauth2' | 'azure-40Seas') => {
        return new Promise<LoginResult>((resolve, reject) =>
            webAuth.popup.authorize(
                {
                    connection,
                    domain: setup.auth0!.domain,
                    responseType: 'token',
                    redirectUri: process.env.AUTH_CALLBACK_URL! + `?origin=${popupOrigin}`,
                },
                (err, result) => {
                    if (result?.accessToken) {
                        resolve({ accessToken: result.accessToken });
                    } else {
                        reject(err?.description);
                    }
                },
            ),
        );
    };

    const loginWithGoogle = () => loginWithSocialPopup('google-oauth2');

    const loginWithMicrosoft = () => loginWithSocialPopup('azure-40Seas');

    return (
        <LoginContext.Provider
            value={{
                signup,
                login,
                loginWithGoogle,
                loginWithMicrosoft,
            }}
        >
            {children}
        </LoginContext.Provider>
    );
};
